// src/components/auth/authService.js

import { InteractionRequiredAuthError } from '@azure/msal-browser'

import {
  clearMsalBrowserStorage,
  getPrimaryAccount,
  isPrimaryAccount,
  loginRequest,
  silentTokenRequest,
} from './authConfig'
import { msalInstance } from '../../index'
import { roles } from './roleTypes'

let interactiveTokenRedirectStarted = false

function getCurrentAccount() {
  const primaryAccount = getPrimaryAccount(msalInstance)

  if (primaryAccount) {
    msalInstance.setActiveAccount(primaryAccount)
    return primaryAccount
  }

  /*
   * Important:
   * Do NOT fall back to accounts[0].
   *
   * accounts[0] may be an old B2C account from:
   * - a previous policy
   * - a previous tenant
   * - the signup authority
   * - an old dev/live environment
   *
   * Using that account is what causes:
   * "Authority mismatch error"
   */
  msalInstance.setActiveAccount(null)
  return null
}

export async function getTenantName() {
  const user = getTokenClaims()
  return user?.defaultImporterId
}

export function getTokenClaims() {
  const account = getCurrentAccount()

  if (!isPrimaryAccount(account)) {
    return null
  }

  return account?.idTokenClaims
}

export async function getUserInfo() {
  const user = getTokenClaims()

  if (!user) {
    return null
  }

  return {
    id: user.sub,
    fullName: `${user.given_name || ''} ${user.family_name || ''}`.trim(),
    firstName: user.given_name,
    lastName: user.family_name,
    jobTitle: user.job_title,
    companyName: user.companyName,
    addressline1: user.streetAddress,
    address: [user.streetAddress, user.city, user.postalCode].filter(Boolean).join(', '),
    city: user.city,
    postcode: user.postalCode,
    email: user.signInName,
    appTenants: user.appTenants,
    tenantName: user.tenantName,
    defaultImporterId: user.defaultImporterId,
    defaultImporterId2: user.DefaultImporterId,
    manufacturerId: user.relatedManufacturerIds,
    importerIds: user.importerIds,
    exp: user.exp,
  }
}

export async function getUserTenantMemberships() {
  const user = getTokenClaims()
  return user?.appTenants || []
}

export function getUserRoles() {
  const user = getTokenClaims()
  return user?.roles || []
}

export async function getAndCheckForUserRole(roleName) {
  const userRoles = getUserRoles()
  return isUserTypeInRoles(userRoles, roleName)
}

export async function isInUserRole(roleName) {
  const userRoles = getUserRoles()
  return userRoles.includes(roleName)
}

Object.flatten = function (data) {
  const result = {}

  function recurse(cur, prop) {
    if (Object(cur) !== cur) {
      result[prop] = cur
    } else if (Array.isArray(cur)) {
      for (let i = 0, l = cur.length; i < l; i += 1) {
        recurse(cur[i], `${prop}[${i}]`)
      }

      if (cur.length === 0) {
        result[prop] = []
      }
    } else {
      let isEmpty = true

      for (const p in cur) {
        if (Object.prototype.hasOwnProperty.call(cur, p)) {
          isEmpty = false
          recurse(cur[p], prop ? `${prop}.${p}` : p)
        }
      }

      if (isEmpty && prop) {
        result[prop] = {}
      }
    }
  }

  recurse(data, '')
  return result
}

function isUserTypeInRoles(userRoles, roleName) {
  if (!Array.isArray(userRoles) || userRoles.length === 0) {
    return false
  }

  return userRoles.includes(roleName)
}

export async function isAdmin() {
  return isUserTypeInRoles(getUserRoles(), 'admin')
}

export async function isImporter() {
  return isUserTypeInRoles(getUserRoles(), 'importer')
}

export async function isImporterAccounts() {
  return isUserTypeInRoles(getUserRoles(), 'importer-accounts')
}

export async function isManufacturer() {
  return isUserTypeInRoles(getUserRoles(), 'manufacturer')
}

/*
 * Used for authenticated API requests.
 *
 * Important:
 * - Silent token acquisition returns to /auth-silent.html, not the React dashboard.
 * - Only one interactive token redirect may be started, even if several API
 *   requests fail authentication simultaneously while the dashboard loads.
 * - Never use msalInstance.getAllAccounts()[0] here.
 */
export async function bearerToken() {
  const account = getCurrentAccount()

  if (!account) {
    if (interactiveTokenRedirectStarted) {
      return null
    }

    interactiveTokenRedirectStarted = true

    await msalInstance.loginRedirect({
      ...loginRequest,
      prompt: 'select_account',
    })

    return null
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...silentTokenRequest,
      account,
    })

    return response.accessToken
  } catch (error) {
    const isAuthorityMismatch =
      error?.errorCode === 'authority_mismatch' ||
      error?.message?.toLowerCase().includes('authority mismatch') ||
      error?.errorMessage?.toLowerCase().includes('authority mismatch')

    if (isAuthorityMismatch) {
      /*
       * Browser has a stale MSAL/B2C account/token from an old policy or authority.
       * Clear it and force a fresh login through the current primary authority.
       */
      clearMsalBrowserStorage({ keepAuthCacheVersion: false })
      msalInstance.setActiveAccount(null)

      if (interactiveTokenRedirectStarted) {
        return null
      }

      interactiveTokenRedirectStarted = true

      await msalInstance.loginRedirect({
        ...loginRequest,
        prompt: 'select_account',
      })

      return null
    }

    const needsInteraction =
      error instanceof InteractionRequiredAuthError ||
      error?.errorCode === 'consent_required' ||
      error?.errorCode === 'interaction_required' ||
      error?.errorCode === 'login_required'

    if (!needsInteraction) {
      throw error
    }

    /*
     * Several dashboard requests can arrive together. Do not allow every
     * request to start its own redirect flow.
     */
    if (interactiveTokenRedirectStarted) {
      return null
    }

    interactiveTokenRedirectStarted = true

    try {
      await msalInstance.acquireTokenRedirect({
        ...loginRequest,
        account,
      })

      return null
    } catch (redirectError) {
      interactiveTokenRedirectStarted = false

      if (redirectError?.errorCode === 'interaction_in_progress') {
        return null
      }

      throw redirectError
    }
  }
}

export async function getRolesCanReceive(userRole) {
  const outputRoles = []

  const testRole = roles.find((x) => x.value === userRole)

  testRole?.receiveRoles?.forEach((receiveRole) => {
    if (!outputRoles.includes(receiveRole)) {
      const output = roles.find((x) => x.value === receiveRole)

      if (output) {
        outputRoles.push(output)
      }
    }
  })

  return outputRoles
}
